import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * How far one member has got through one course (matrix rows 1–2).
 *
 * **Partial progress is a first-class stored fact, not an absence.** Row 2 is
 * the requirement: some lessons done means no completion and no grant, *and*
 * progress that survives — a member who reads three of eight lessons and comes
 * back next week resumes at four. Deriving it on the fly from
 * `lesson_completions` is exactly what this shape does; what must not happen is
 * a client keeping its own count, because reinstalling the app would then reset
 * a curriculum.
 */
export const CourseProgressSchema = z
  .object({
    courseId: UuidSchema,
    totalLessons: z.number().int().nonnegative(),
    completedLessons: z.number().int().nonnegative(),
    /**
     * The lesson to resume at — the first incomplete one in curriculum order,
     * and `null` when there is nothing left to read.
     *
     * Server-resolved rather than left to the client, so "continue" means the
     * same thing on a phone that has been offline and on a fresh install.
     */
    nextLessonId: UuidSchema.nullable(),
    /** When every lesson was done. `null` while the course is unfinished. */
    completedAt: z.iso.datetime().nullable(),
  })
  .strict();

export type CourseProgress = z.infer<typeof CourseProgressSchema>;
